/* eslint-disable eslint-comments/disable-enable-pair -- remove warning */
/* eslint-disable no-param-reassign -- remove warning */
import React, { useState } from 'react';
import {
  shape, string, bool, func,
} from 'prop-types';
import {
  Button,
  Card,
  Heading,
} from '@americanexpress/dls-react';
import {
  IconAirplane,
  IconBusiness,
  IconCard,
  IconCardBenefit,
  IconConcierge,
  IconDining,
  IconEntertainment,
  IconMerchandise,
  IconLounge,
  IconRewards,
  IconTravelBag,
} from '@americanexpress/dls-icons';
import { OnVisible } from '@americanexpress/data-manager-utils';
import { MembershipTextBadge } from '@americanexpress/web-react-extensions';
import { useReactlytics } from '@americanexpress/reactlytics';
import styles from './AxpBenefitsActivity.scss';
import {
  ENROLL_STATES,
  exploreAmexPartners,
  maximizeMembership,
} from '../constants';
import {
  decodeHtmlEntities,
  getEnrollmentStatusForColor,
} from '../utils/utils';
import { navigateIfMobile } from '../utils/mobileNavigationHelper';

const BrowseBenefits = (props) => {
  const {
    cardName,
    benefitsForActivity,
    viewAllBenefits,
    categorizedBenefits,
    isExpanded = false,
    onToggleExpand,
    renderQuickView,
    languageData,
    locale,
  } = props;
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [showQuickView, setShowQuickView] = useState(false);
  const { track } = useReactlytics();

  const handleOpenQuickView = (benefit) => {
    track({
      event: {
        element: `${cardName}>>enrollment-center>>enroll-now`,
        type: 'click',
        meta: { benefitname: decodeHtmlEntities(benefit?.benefitName) },
      },
    });
    if (navigateIfMobile(benefit)) return;
    setSelectedBenefit(benefit);
    setShowQuickView(true);
  };

  const handleCloseQuickView = () => {
    setShowQuickView(false);
    setSelectedBenefit(null);
  };

  const getEnrollLink = (benefit) => {
    const linkKey = ENROLL_STATES.find((state) => {
      const actions = benefit?.summary?.layout?.[state]?.callToAction;
      return actions?.ENROLL?.link || actions?.LEARNMORE?.link || actions?.OTHER?.link;
    });

    const callToAction = benefit?.summary?.layout?.[linkKey]?.callToAction;
    return callToAction?.ENROLL?.link || callToAction?.LEARNMORE?.link || callToAction?.OTHER?.link;
  };

  const handleClick = (benefitPath) => {
    let basePath = window.location.pathname;
    basePath = basePath.replace('/view-all', '').replace('/activity', '');
    const sanitizedBenefitPath = benefitPath.startsWith('http') ? new URL(benefitPath).pathname : benefitPath;
    const normalizedBenefitPath = sanitizedBenefitPath.startsWith(basePath)
      ? sanitizedBenefitPath
      : `${basePath}/${sanitizedBenefitPath.replace(/^\/+/, '')}`;
    window.location.href = `${window.location.origin}${normalizedBenefitPath}?from=activity`;
  };

  const handleEnrollCTA = (benefit) => {
    const link = getEnrollLink(benefit);
    if (link) {
      handleClick(link);
      handleCloseQuickView();
    }
  };

  const sanitizedCategorizedBenefits = { ...categorizedBenefits };
  delete sanitizedCategorizedBenefits.Enroll;
  if (benefitsForActivity) {
    Object.values(benefitsForActivity)?.forEach((benefit) => {
      if (benefit) {
        benefit.category = Object
          .entries(sanitizedCategorizedBenefits)
          .find(([, ids]) => ids.includes(benefit.benefitName))?.[0] || 'Enroll';
      }
    });
  }

  const internalBenefits = benefitsForActivity
    ? Object.values(benefitsForActivity)?.filter(
      (b) => b?.enrollmentOriginType !== 'EXTERNAL'
        && b?.enrollmentOriginType !== 'NEITHER'
        && b?.enrollmentApplication?.enrollmentStatus !== 'ENROLLED'
    )
    : [];

  const externalBenefits = viewAllBenefits
    ? Object.values(viewAllBenefits)?.filter(
      (b) => b?.enrollmentOriginType === 'EXTERNAL'
        && b?.enrollmentOriginType !== 'NEITHER'
    )
    : [];

  const icons = {
    Travel: <IconAirplane isFilled={true} isColorImportant={true} color="neutral" />,
    'Premium Travel': <IconTravelBag isFilled={true} color="neutral" />,
    Entertainment: <IconEntertainment isFilled={true} color="neutral" />,
    Shopping: <IconMerchandise isFilled={true} color="neutral" />,
    Services: <IconConcierge isFilled={true} color="neutral" />,
    'Membership Rewards': <IconRewards isFilled={true} color="neutral" />,
    Enroll: <IconCard isFilled={true} color="neutral" />,
    Rewards: <IconRewards isFilled={true} color="neutral" />,
    Dining: <IconDining isFilled={true} color="neutral" />,
    'Business Tools': <IconBusiness isFilled={true} color="neutral" />,
    Access: <IconLounge isFilled={true} color="neutral" />,
    Default: <IconCardBenefit isFilled={true} color="neutral" />,
  };

  const getEnrollStatus = (benefit) => {
    const descriptionKey = ENROLL_STATES
      .find((state) => benefit?.summary?.layout?.[state]?.showStatus?.description);
    return benefit?.summary?.layout?.[descriptionKey]?.showStatus?.description;
  };

  const getEnrollLabel = (benefit) => {
    const labelKey = ENROLL_STATES.find((state) => {
      const actions = benefit?.summary?.layout?.[state]?.callToAction;
      return actions?.ENROLL?.label || actions?.LEARNMORE?.label || actions?.OTHER?.label;
    });
    const callToAction = benefit?.summary?.layout?.[labelKey]?.callToAction;
    return callToAction?.ENROLL?.label
      || callToAction?.LEARNMORE?.label
      || callToAction?.OTHER?.label;
  };

  const renderBenefitCards = (benefits) => benefits.map((benefit, index) => {
    const headingId = `card-heading-${index}-${benefit?.benefitTitle?.replace(/\s+/g, '-')}`;
    return (
      <Card
        aria-labelledby={headingId}
        orientation="vertical"
        className="width-full"
        key={benefit?.benefitTitle}
        role="region"
        onClick={() => handleOpenQuickView(benefit)}
      >
        <div className={`${styles.cardFlex} flex`}>
          <div id="card-img">
            <img
              src={benefit?.summary?.marketingText?.imageUrl}
              alt={`Benefit ${index + 1}`}
              className={styles.cardImage}
            />
          </div>
          <div className={styles.cardTitle}>
            <p data-testid="" className="margin-1-b">
              {icons[benefit?.category]}&nbsp;{benefit?.category}
            </p>
            <Heading level={3} variant="sans-small-bold" id={headingId}>
              {benefit?.benefitTitle && decodeHtmlEntities(benefit?.benefitTitle)}
            </Heading>
            {getEnrollStatus(benefit) && (
              <MembershipTextBadge
                className="flex pad-1-tb"
                status={getEnrollmentStatusForColor(benefit)}
                size="small"
                type="text"
              >
                {getEnrollStatus(benefit)}
              </MembershipTextBadge>
            )}
          </div>
        </div>
        <div>
          <p className={styles.cardText}>
            {benefit?.summary?.marketingText?.copy
              && decodeHtmlEntities(benefit?.summary?.marketingText?.copy)}
          </p>
        </div>
        <div className={styles.cardText}>
          <Button
            variant="primary"
            data-testid={`enroll-button-${index}`}
            className={styles.cardButton}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenQuickView(benefit);
            }}
          >
            {getEnrollLabel(benefit) || 'Enroll Now'}
          </Button>
        </div>
      </Card>
    );
  });

  // Determine which benefits to display based on expanded state:
  // - Internal section shows all when expanded, or first 2 on initial load
  // - Partner section shows when expanded OR when there are fewer than 2 internal benefits
  //   (e.g. 0 or 1), ensuring the section is always sufficiently filled on initial load
  const visibleInternalBenefits = isExpanded ? internalBenefits : internalBenefits.slice(0, 2);
  const visibleExternalBenefits = isExpanded ? externalBenefits : externalBenefits.slice(0, 2);
  const showPartnerSection = externalBenefits.length > 0
    && (isExpanded || internalBenefits.length < 2);

  return (
    <>
      {onToggleExpand && (
        <div className={styles.cardContainer}>
          {renderBenefitCards(visibleInternalBenefits)}
          {/* The View More/View Less button is now handled by the parent component */}
        </div>
      )}
      {showPartnerSection && (
        <div id="exploremore">
          <OnVisible
            analyticsInfo={{
              element: `${cardName}>>partner-enrollment-component`,
              type: 'impression',
            }}
          >
            <Heading
              level={3}
              variant="sans-small-bold"
              className="margin-3-t color-text-regular"
            >
              {exploreAmexPartners}
            </Heading>
          </OnVisible>
          <p className="margin-2-tb">
            {maximizeMembership}
          </p>
          <div
            className={
              externalBenefits.length > 1
                ? styles.cardContainer
                : `${styles.cardContainer} ${styles.maxWidth50} fluid-md-down`
            }
          >
            {renderBenefitCards(visibleExternalBenefits)}
          </div>
        </div>
      )}
      {selectedBenefit && showQuickView && renderQuickView({
        benefit: selectedBenefit,
        benefitsDataForQuickView: viewAllBenefits,
        benefitNamesListFromActivity: Object.keys(viewAllBenefits),
        languageData,
        locale,
        cardName,
        toggleModal: handleCloseQuickView,
        trackerData: null,
        onEnrollCTA: () => handleEnrollCTA(selectedBenefit),
      })}
    </>
  );
};

export default BrowseBenefits;

BrowseBenefits.propTypes = {
  benefitsForActivity: shape({}),
  viewAllBenefits: shape({}),
  categorizedBenefits: shape({}),
  cardName: string.isRequired,
  isExpanded: bool,
  onToggleExpand: func,
  renderQuickView: func.isRequired,
  languageData: shape({}).isRequired,
  locale: string.isRequired,
};
